/**
 * Forma estandar de error que circula por la app.
 * Cualquier error que sale del cliente HTTP cumple esta forma — los componentes
 * nunca ven un AxiosError raw.
 */
export type ApiError = {
  statusCode: number;
  /** Codigo de negocio o tecnico: 'VALIDATION_ERROR', 'NETWORK_ERROR', etc. */
  code: string;
  /** Mensajes presentables al usuario. Multiple para soportar Zod-style. */
  message: string[];
  /** Errores por campo cuando aplica (forms). */
  fieldErrors?: Record<string, string>;
  /**
   * Segundos que el usuario debe esperar antes de reintentar. Presente en
   * respuestas de rate-limit/lockout (HTTP 429 y bloqueos de cuenta). Lo puebla
   * `normalizeError` leyendo el header `Retry-After` o `metadata.secondsRemaining`
   * del cuerpo. Ausente si el backend no informó tiempo.
   */
  retryAfterSeconds?: number;
  /** Detalle interno para debugging — no mostrar al usuario. */
  details?: unknown;
};

export type ApiResponse<T> = {
  data: T;
};

/**
 * Meta de paginación page-based. Debe espejar `PaginationMeta` del
 * backend (`shared/pagination/pagination.dto.ts`): cualquier listado del
 * BE que retorne `{items, meta}` cumple esta forma.
 */
export type PaginationMeta = {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

/**
 * Envelope estándar de listados paginados. Reutilizable por cualquier
 * adapter de `entities/*` que consuma un endpoint con paginación
 * server-side — evita re-declarar `PaginationMeta` por dominio.
 */
export type PaginatedResult<T> = {
  items: T[];
  meta: PaginationMeta;
};

/** Type guard para distinguir un ApiError de cualquier otro error capturado. */
export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'statusCode' in value &&
    'code' in value &&
    'message' in value &&
    Array.isArray((value as ApiError).message)
  );
}
